import { orderBy } from "lodash-es";

import type { AppStore } from "@/store/AppStore";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording";
import { clientEnvModule } from "@/modules/client-env";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import {
  type MediaRecorderTranscriptPartialTurn,
  TranscriptionStatus,
} from "@/store/audio/recording";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds";
import {
  getNoteSuggestionContentReadiness,
  NoteSuggestionContentState,
} from "@/store/note/helpers/note-suggestion-content";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";
import type { AudioCaptureToolbarPrimaryAction } from "@/store/audio/pipeline/audio-capture.types";
import { projectCapturePipelineUiState } from "@/store/audio/pipeline/audio-pipeline.ui-projection";
import type {
  AudioCaptureEnvironmentState,
  AudioCaptureMicrophoneState,
  AudioCaptureRecordingState,
  AudioCaptureTranscriptEntry,
  AudioCaptureTranscriptState,
} from "./audio-capture-actions.types";

const canProcessRecording = ({
  phase,
  isMicrophoneDisconnected,
  canProcessNoteContent,
}: {
  phase: AudioCapturePhase;
  isMicrophoneDisconnected: boolean;
  canProcessNoteContent: boolean;
}): boolean => {
  if (isMicrophoneDisconnected || !canProcessNoteContent) {
    return false;
  }

  if (
    phase === AudioCapturePhase.Paused ||
    phase === AudioCapturePhase.Recording ||
    phase === AudioCapturePhase.Stopped
  ) {
    return true;
  }

  return false;
};

function deriveToolbarPrimaryAction({
  canProcess,
  isProcessing,
  retryTargets,
}: {
  canProcess: boolean;
  isProcessing: boolean;
  retryTargets: {
    recordAgain: boolean;
    upload: boolean;
    retranscribe: boolean;
    transform: boolean;
    retryAll: boolean;
  };
}): AudioCaptureToolbarPrimaryAction {
  if (retryTargets.recordAgain) {
    return {
      kind: "record-again",
      ariaLabel: "Record again",
    };
  }

  if (retryTargets.retryAll) {
    return {
      kind: "retry-all",
      ariaLabel: "Retry all processing",
    };
  }

  if (retryTargets.upload || retryTargets.retranscribe || retryTargets.transform) {
    return {
      kind: "retry",
      ariaLabel: retryTargets.upload ? "Retry upload and processing" : "Retry processing",
    };
  }

  return {
    kind: "done",
    isDisabled: !canProcess || isProcessing,
  };
}

export interface AudioCaptureSourceSnapshot {
  recording: Omit<AudioCaptureRecordingState, "waveform">;
  ui: {
    showOnboardingTooltip: boolean;
    shouldShowDesktopDownloadNotice: boolean;
  };
  environment: AudioCaptureEnvironmentState;
  visualizer: AudioLevelProbe | null;
  recordingId: string | null;
}

export function normalizeTranscriptEntries(
  finalTurns: LocalAudioRecordingTranscriptTurnRecord[],
  livePartialTurns: MediaRecorderTranscriptPartialTurn[] = []
): AudioCaptureTranscriptEntry[] {
  const sortedTurns = orderBy(
    buildDisplayTranscriptTurns({
      finalTurns,
      livePartialTurns,
    }),
    turn => turn.turnOrder,
    "asc"
  );

  return sortedTurns.map((turn, index) => {
    const text = turn.words.map(word => word.text).join(" ");
    const nextTurn = sortedTurns[index + 1];
    const channel = turn.channel;
    const nextChannel = typeof nextTurn?.channel === "number" ? nextTurn.channel : null;

    return {
      id: turn.id,
      alignment:
        channel < 0
          ? MDSBubbleAlignment.Center
          : channel > 0
            ? MDSBubbleAlignment.Left
            : MDSBubbleAlignment.Right,
      variant:
        channel < 0
          ? MDSBubbleVariant.Transparent
          : channel === 0
            ? MDSBubbleVariant.VoiceSelf
            : MDSBubbleVariant.VoiceOthers,
      text,
      isLastInGroup: channel !== nextChannel,
    } satisfies AudioCaptureTranscriptEntry;
  });
}

const legacyTranscriptLabel = (channel: number): string =>
  channel === 0 ? "Me" : "Other Speakers";

const buildFullTranscript = ({
  finalTurns,
  livePartialTurns,
}: {
  finalTurns: LocalAudioRecordingTranscriptTurnRecord[];
  livePartialTurns: MediaRecorderTranscriptPartialTurn[];
}): string => {
  const displayTurns = orderBy(
    buildDisplayTranscriptTurns({
      finalTurns,
      livePartialTurns,
    }),
    turn => turn.turnOrder,
    "asc"
  );
  if (!displayTurns.length) return "";

  return displayTurns
    .map(turn => {
      const words = turn.words.map(word => word.text).join(" ");
      return `[${legacyTranscriptLabel(turn.channel)}]: ${words}`;
    })
    .join("\n");
};

type DisplayTranscriptTurn = {
  id: string;
  channel: number;
  turnOrder: number;
  words: Array<{ text: string }>;
};

const buildDisplayTranscriptTurns = ({
  finalTurns,
  livePartialTurns,
}: {
  finalTurns: LocalAudioRecordingTranscriptTurnRecord[];
  livePartialTurns: MediaRecorderTranscriptPartialTurn[];
}): DisplayTranscriptTurn[] => {
  const turnsByOrder = new Map<number, DisplayTranscriptTurn>();

  for (const partialTurn of livePartialTurns) {
    turnsByOrder.set(partialTurn.turnOrder, {
      id: `partial-${partialTurn.turnOrder}`,
      channel: partialTurn.channel,
      turnOrder: partialTurn.turnOrder,
      words: (partialTurn.words ?? []).map(word => ({
        text: word.word,
      })),
    });
  }

  for (const finalTurn of finalTurns) {
    turnsByOrder.set(finalTurn.transcriptTurnIndex, {
      id: finalTurn.id,
      channel: finalTurn.channel,
      turnOrder: finalTurn.transcriptTurnIndex,
      words: (finalTurn.content?.words ?? []).map(word => ({
        text: word.text,
      })),
    });
  }

  return [...turnsByOrder.values()];
};

export function createAudioCaptureSourceSnapshot(
  store: AppStore,
  { noteId }: { noteId: string }
): AudioCaptureSourceSnapshot {
  const audioStore = store.audio;
  const recordingStore = audioStore.recording;

  const noteRecording = store.audioRecordings.getNoteRecording({ noteId });
  const recordingObservable = noteRecording.recording as AudioRecordingObservable | undefined;
  const recordingId = recordingObservable?.id ?? null;

  const transcriptTurns = recordingObservable?.transcriptTurns ?? [];
  const liveTranscriptPartials = recordingObservable?.liveTranscriptPartials ?? [];
  const transcriptEntries = normalizeTranscriptEntries(transcriptTurns, liveTranscriptPartials);

  const transcript: AudioCaptureTranscriptState = {
    entries: transcriptEntries,
    fullText: buildFullTranscript({
      finalTurns: transcriptTurns,
      livePartialTurns: liveTranscriptPartials,
    }),
    hasTranscript: transcriptEntries.length > 0,
    isDisconnected: recordingId
      ? recordingStore.getTranscriptionStatus(recordingId) === TranscriptionStatus.Disconnected
      : false,
    isCompromised: recordingId
      ? recordingStore.getTranscriptionStatus(recordingId) === TranscriptionStatus.Compromised
      : false,
    isTranscriptionUnsupported: recordingStore.transcriptIsUnsupported,
  };

  const microphone: AudioCaptureMicrophoneState = {
    label: recordingStore.activeAudioInputDeviceLabel,
    isDisconnected: recordingStore.isMicrophoneDisconnected,
    isAudioSilent: recordingStore.isAudioSilent,
  };

  const noteObservable = store.notes.getNoteObservableById({ noteId });
  const snapshot = recordingId ? audioStore.pipeline.getSnapshot(recordingId) : null;
  const pipelineUi = projectCapturePipelineUiState({
    snapshot,
    recording: recordingObservable ?? null,
    isInitializing: recordingStore.isInitializing,
    isRecordingPaused: recordingStore.isPaused,
  });

  const noteContentReadiness = getNoteSuggestionContentReadiness(noteObservable);
  const canProcessNoteContent = noteContentReadiness.state === NoteSuggestionContentState.Ready;

  const canProcess = canProcessRecording({
    phase: pipelineUi.phase,
    isMicrophoneDisconnected: recordingStore.isMicrophoneDisconnected,
    canProcessNoteContent,
  });

  const primaryAction = deriveToolbarPrimaryAction({
    canProcess,
    isProcessing: pipelineUi.isProcessing,
    retryTargets: pipelineUi.retryTargets,
  });
  const toolbar = {
    processingLabel: pipelineUi.processingLabel,
    primaryAction,
    showCancel: pipelineUi.isFinished && !pipelineUi.isProcessing,
    retryTargets: {
      upload: pipelineUi.retryTargets.upload,
      transform: pipelineUi.retryTargets.transform || pipelineUi.retryTargets.retranscribe,
      retryAll: pipelineUi.retryTargets.retryAll,
    },
  };

  const recordingBase: Omit<AudioCaptureRecordingState, "waveform"> = {
    phase: pipelineUi.phase,
    durationLabel: recordingObservable?.formattedDuration ?? "00:00",
    transcript,
    microphone,
    isProcessing: pipelineUi.isProcessing,
    isFinished: pipelineUi.isFinished,
    activeMicrophoneLabel: recordingStore.activeAudioInputDeviceLabel,
    noteContentState: noteContentReadiness.state,
    toolbar,
    failureMessage: pipelineUi.failureMessage,
  };

  const ui = {
    showOnboardingTooltip: audioStore.ui.showOnboardingTooltip,
    shouldShowDesktopDownloadNotice: !clientEnvModule.isDesktop(),
  };

  const environment: AudioCaptureEnvironmentState = {
    isAdmin: isMemAccount(store.account.myAccount),
    isDesktop: store.interface.isDesktop,
  };

  return {
    recording: recordingBase,
    ui,
    environment,
    visualizer: recordingStore.visualizer,
    recordingId,
  };
}
